import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaCheckCircle, FaSpinner, FaExclamationCircle } from 'react-icons/fa';

export default function SubscriptionSuccess() {
  const { API, user } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 10) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const getPlanName = (planId) => {
    const names = {
      'decouverte': 'Découverte',
      'pro': 'Pro',
      'fournisseur': 'Fournisseur',
      'sur_mesure': 'Sur Mesure'
    };
    return names[planId] || planId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F5F5] to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <FaSpinner className="text-6xl text-[#0A2540] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-[#0A2540] mb-4">
                Vérification du paiement...
              </h1>
              <p className="text-gray-600 mb-4">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#0A2540] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(pollCount * 10, 90)}%` }}
                />
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-[#635BFF]/10 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-6xl text-[#635BFF]" />
              </div>
              <h1 className="text-2xl font-bold text-[#635BFF] mb-4">
                Paiement Réussi ! 🎉
              </h1>
              <p className="text-gray-600 mb-6">
                Votre abonnement <strong>{getPlanName(paymentData?.plan)}</strong> est maintenant actif.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-600">
                  <strong>Plan :</strong> {getPlanName(paymentData?.plan)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Montant :</strong> {paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '—'}€
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Validité :</strong> 1 an
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  className="w-full bg-[#0A2540] hover:bg-[#003d56]"
                  onClick={() => navigate('/user/subscription')}
                >
                  Voir mon abonnement
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  Retour à l'accueil
                </Button>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="bg-red-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                <FaExclamationCircle className="text-6xl text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-red-600 mb-4">
                Paiement Échoué
              </h1>
              <p className="text-gray-600 mb-6">
                Le paiement n'a pas pu être confirmé. Veuillez réessayer ou contacter notre support.
              </p>

              <div className="space-y-3">
                <Button 
                  className="w-full bg-[#0A2540] hover:bg-[#003d56]"
                  onClick={() => navigate('/pricing')}
                >
                  Réessayer
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  Retour à l'accueil
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
